import React from 'react';

import { Text } from '@zapier/design-system';

import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { constants } from 'app/entities/EnvironmentVariable';

export const SETUP_STEPS = {
  basic: {
    fields: 'Step 1',
    configuration: 'Step 2',
    testing: 'Step 3',
  },
  oauth2: {
    fields: 'Step 1',
    redirectUri: 'Step 2',
    credentials: 'Step 3',
    configuration: 'Step 4',
    testing: 'Step 5',
  },
  session: {
    fields: 'Step 1',
    tokenExchange: 'Step 2',
    configuration: 'Step 3',
    testing: 'Step 4',
  },
  custom: {
    fields: 'Step 1',
    configuration: 'Step 2',
    testing: 'Step 3',
  },
  digest: {
    fields: 'Step 1',
    configuration: 'Step 2',
    testing: 'Step 3',
  },
};

export const AUTH_TYPE_HELP_TEXT = {
  basic: {
    description: (
      <Text>
        Basic Auth includes a pre-built form where users enter a username and
        password to authenticate app accounts, which Zapier then includes in
        every API call to that app. You only need to add a test API call to
        enable Basic Auth.{' '}
        <Link href={`${BASE_DOC_URL_VB}/build/basicauth`} target="_blank">
          Learn more
        </Link>
        .
      </Text>
    ),
    heading: 'Basic Auth',
  },
  session: {
    description: (
      <Text>
        Session Auth includes a pre-built form where users enter a username and
        password, along with any optional form fields added below. Zapier passes
        that data to your API’s token exchange URL to exchange those credentials
        for an authentication token.{' '}
        <Link href={`${BASE_DOC_URL_VB}/build/sessionauth`} target="_blank">
          Learn more
        </Link>
        .
      </Text>
    ),
    heading: 'Session Auth',
  },
  custom: {
    description: (
      <Text>
        API Key Auth lets you build a form to request an API key, along with any
        additional fields your API requires for authentication. Zapier then
        passes the data users enter in those fields with every API call.{' '}
        <Link href={`${BASE_DOC_URL_VB}/build/apikeyauth`} target="_blank">
          Learn more
        </Link>
        .
      </Text>
    ),
    heading: 'API Key Auth',
  },
  oauth2: {
    description: (
      <Text>
        Oauth v2 authentication redirects users to your API’s site where they
        can authorize Zapier to access their account which sends a request token
        to Zapier that is exchanged for an access token.{' '}
        <Link href={`${BASE_DOC_URL_VB}/build/oauth`} target="_blank">
          Learn more
        </Link>
        .
      </Text>
    ),
    heading: 'OAuth v2',
  },
  digest: {
    description: (
      <Text>
        Digest Auth includes a pre-built form where users enter a username and
        password, optionally along with any form fields added below. Zapier
        requests a nonce key before each API call, then uses it to pass the
        credentials along with the API call for the Zap step.{' '}
        <Link href={`${BASE_DOC_URL_VB}/build/digestauth`} target="_blank">
          Learn more
        </Link>
        .
      </Text>
    ),
    heading: 'Digest Auth',
  },
};

const NEEDS_FIELDS_TEXT =
  'You must define at least one field where your users can enter API credentials. Your authentication configuration is not complete until you have done so.';

export const INPUT_FIELDS_ADDITIONAL_HELP_TEXT = {
  custom: NEEDS_FIELDS_TEXT,
  session: NEEDS_FIELDS_TEXT,
  oauth2:
    'If your OAuth v2 implementation is standard you don’t need to make any changes here!',
  legacy:
    'The URL may be different after converting your legacy integration. Be sure to update your app with the new URL below.',
};

const DEFAULT_CONNECTION_LABEL = (
  <span>
    Customize the connection label users see in Zapier to help differentiate
    between multiple connected accounts for your app. If included, do not use{' '}
    <Link
      href={`https://docs.zapier.com/platform/publish/integration-publishing-requirements#5-6-integration-has-a-valid-connection-label`}
      target="_blank"
    >
      sensitive information
    </Link>
    . Include info from authentication input fields with{' '}
    <code>{'{{bundle.authData.field}}'}</code> or from test request output
    fields with <code>{'{{bundle.inputData.field}}'}</code>, replacing{' '}
    <code>field</code> with the field key you wish to use.{' '}
    <Link href={`${BASE_DOC_URL_VB}/build/connection-label`} target="_blank">
      Learn More
    </Link>
    .
  </span>
);

const AUTH_FIELDS_REQUIRED_HEADING = (
  <span className="required-text">Authentication Fields</span>
);
const AUTH_FIELDS_OPTIONAL_HEADING = 'Use Authentication Fields to:';

export const STEPS_HELP_BY_TYPE = {
  basic: {
    configureToken: undefined,
    connectionLabel: (
      <Text>
        Customize the connection label users see in Zapier to help differentiate
        between multiple connected accounts for your app. If included, do not
        use{' '}
        <Link
          href={`https://docs.zapier.com/platform/publish/integration-publishing-requirements#5-6-integration-has-a-valid-connection-label`}
          target="_blank"
        >
          sensitive information
        </Link>
        . Include {'{{bundle.authData.username}}'} to include the username, or{' '}
        {'{{bundle.inputData.field}}'} to reference fields from your Test API
        call, replacing field with the field name to use in the label.{' '}
        <Link
          anchor="#label"
          href={`${BASE_DOC_URL_VB}/build/auth`}
          target="_blank"
        >
          Learn More
        </Link>
        .
      </Text>
    ),
    credentials: undefined,
    description: undefined,
    fields: {
      heading: AUTH_FIELDS_OPTIONAL_HEADING,
      description: (
        <Text>
          Build a form with any additional fields your API requires for
          authentication. Zapier includes <strong>username</strong> and{' '}
          <strong>password</strong> by default.
        </Text>
      ),
    },
    endpoints: {
      description:
        'The only things needed for Basic auth are a test API call to verify users’ credentials, and a connection label to identify accounts. Zapier automatically configures everything else, and includes the username and password in API calls automatically.',
    },
    redirectUrl: undefined,
  },
  basicLegacy: {
    configureToken: undefined,
    connectionLabel: (
      <Text>
        Customize the connection label users see in Zapier to help differentiate
        between multiple connected accounts for your app. If included, do not
        use{' '}
        <Link
          href={`https://docs.zapier.com/platform/publish/integration-publishing-requirements#5-6-integration-has-a-valid-connection-label`}
          target="_blank"
        >
          sensitive information
        </Link>
        . Include {'{{bundle.authData.username}}'} to include the username, or{' '}
        {'{{bundle.inputData.field}}'} to reference fields from your Test API
        call, replacing field with the field name to use in the label.{' '}
        <Link
          anchor="#label"
          href={`${BASE_DOC_URL_VB}/build/auth`}
          target="_blank"
        >
          Learn More
        </Link>
        .
      </Text>
    ),
    credentials: undefined,
    description: undefined,
    fields: {
      heading: AUTH_FIELDS_OPTIONAL_HEADING,
      description: (
        <Text>
          We’ll map these fields to <strong>username</strong> and{' '}
          <strong>password</strong> according to the auth mapping carried over
          from your legacy project.
        </Text>
      ),
    },
    endpoints: {
      description:
        'The only things needed for Basic auth are a test API call to verify users’ credentials, and a connection label to identify accounts. Zapier automatically configures everything else, and includes the username and password in API calls automatically.',
    },
    redirectUrl: undefined,
  },
  session: {
    configureToken: (
      <Text>
        Enter your Token Exchange URL where Zapier can exchange user credentials
        for the token. Zapier automatically includes the data from your input
        form in the request body; click Show Options to customize the API call,
        and type {'{{'} in any value field to see bundle data field options.{' '}
        <Link
          anchor="#access"
          href={`${BASE_DOC_URL_VB}/build/sessionauth`}
          target="_blank"
        >
          Learn More
        </Link>
        .
      </Text>
    ),
    connectionLabel: DEFAULT_CONNECTION_LABEL,
    credentials: undefined,
    description: undefined,
    endpoints: {
      description: (
        <Text>
          Add a simple API endpoint to test user credentials. Include the token
          in the URL Params or HTTP Headers as required by your API with{' '}
          {'{{bundle.authData.accessToken}}'}, replacing accessToken with your
          API’s field name for the token.{' '}
          <Link
            anchor="#test"
            href={`${BASE_DOC_URL_VB}/build/sessionauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    fields: {
      heading: AUTH_FIELDS_REQUIRED_HEADING,
      description: (
        <Text>
          Build a form with fields for each item your API requires for
          authentication, such as username, password, subdomain, and more.
          Zapier does not include any field by default.{' '}
          <Link
            anchor="#form"
            href={`${BASE_DOC_URL_VB}/build/sessionauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    redirectUrl: undefined,
  },
  custom: {
    configureToken: undefined,
    connectionLabel: DEFAULT_CONNECTION_LABEL,
    credentials: undefined,
    description: undefined,
    endpoints: {
      description: (
        <Text>
          Add a simple API endpoint to test user credentials. Zapier includes
          data from your input form in the URL Params by default; click Show
          Options to customize the API call if your API expects them in the
          header instead.{' '}
          <Link
            anchor="#request"
            href={`${BASE_DOC_URL_VB}/build/apikeyauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    fields: {
      heading: AUTH_FIELDS_REQUIRED_HEADING,
      description: (
        <Text>
          Build a form with fields for each item your API requires for
          authentication, including a field for your API key and additional
          field for any other data needed. Zapier does not include any fields by
          default.{' '}
          <Link
            anchor="#form"
            href={`${BASE_DOC_URL_VB}/build/apikeyauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    redirectUrl: undefined,
  },
  oauth2: {
    configureToken: undefined,
    connectionLabel: DEFAULT_CONNECTION_LABEL,
    credentials: {
      description: (
        <Text>
          From your app’s API or developer settings, copy the Client ID and
          Secret from your app and paste them below.{' '}
          <Link
            anchor="#add-application-credentials-to-zapier"
            href={`${BASE_DOC_URL_VB}/build/oauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    description: undefined,
    endpoints: {
      description: (
        <Text>
          Add the Authorization URL from your API—no additional settings are
          typically needed—optionally with comma-separated scopes. Then add your
          API’s Access Token request and refresh endpoints. Zapier includes the
          default fields, though click Show options to customize if needed.
          Finally add a test API call to test your authentication, and a
          connection label to identify accounts.{' '}
          <Link
            anchor="#add-oauth-endpoint-configuration"
            href={`${BASE_DOC_URL_VB}/build/oauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    fields: {
      heading: AUTH_FIELDS_OPTIONAL_HEADING,
      description: (
        <Text>
          If your API’s OAuth Authorization URL does not require details about
          the user, click Continue to skip this step. Otherwise, add input
          fields for each item—such as subdomain or team name—that your API
          requires to show the Authorization page.{' '}
          <Link
            anchor="#optional-input-form"
            href={`${BASE_DOC_URL_VB}/build/oauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    redirectUrl: {
      description: (
        <Text>
          Copy the link below, then use it in your app’s API or developer
          settings to create a new integration or app to use with Zapier.{' '}
          <Link
            anchor="#oauth-redirect-url"
            href={`${BASE_DOC_URL_VB}/build/oauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
  },
  digest: {
    configureToken: undefined,
    connectionLabel: DEFAULT_CONNECTION_LABEL,
    credentials: undefined,
    description: undefined,
    endpoints: {
      description: (
        <Text>
          Add a simple API endpoint to test user credentials. Click Show Options
          to customize the data Zapier sends your API call, and type {'{{'} in
          any value field to see bundle data field options.{' '}
          <Link
            anchor="#test"
            href={`${BASE_DOC_URL_VB}/build/digestauth`}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    fields: {
      heading: AUTH_FIELDS_OPTIONAL_HEADING,
      description: (
        <Text>
          Zapier automatically includes a form to request usernames and
          passwords for authentication with Digest Auth. If your API requires
          additional fields, add them here. Otherwise, continue to Step 2.{' '}
          <Link
            anchor="#form"
            href={`${BASE_DOC_URL_VB}/build/digestauth`}
            target="_blank"
          >
            {' '}
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    redirectUrl: undefined,
  },
  pkce: {
    configureToken: undefined,
    connectionLabel: DEFAULT_CONNECTION_LABEL,
    credentials: {
      description: (
        <Text>
          PKCE (Proof Key for Code Exchange) is an extension of OAuth v2. It
          helps prevent certain types of attacks, such as authorization code
          interception.{' '}
          <Link
            anchor="#credentials"
            href={'https://oauth.net/2/pkce/'}
            target="_blank"
          >
            Learn More
          </Link>
          .
        </Text>
      ),
    },
    description: undefined,
    endpoints: undefined,
    fields: undefined,
    redirectUrl: undefined,
  },
  none: {
    configureToken: undefined,
    connectionLabel: undefined,
    credentials: undefined,
    description: undefined,
    endpoints: undefined,
    fields: undefined,
    redirectUrl: undefined,
  },
};

export const DEFAULT_REQUEST_CONFIGS = {
  oauth2Legacy: {
    [constants.OAUTH_CLIENT_ID]: '',
    [constants.OAUTH_CLIENT_SECRET]: '',
  },
  oauth2: {
    [constants.OAUTH_CLIENT_ID]: '',
    [constants.OAUTH_CLIENT_SECRET]: '',
    authorizeUrl: {
      params: {
        client_id: `{{process.env.${constants.OAUTH_CLIENT_ID}}}`,
        state: '{{bundle.inputData.state}}',
        redirect_uri: '{{bundle.inputData.redirect_uri}}',
        response_type: 'code',
      },
    },
    getAccessToken: {
      method: 'POST',
      body: {
        code: '{{bundle.inputData.code}}',
        client_id: `{{process.env.${constants.OAUTH_CLIENT_ID}}}`,
        client_secret: `{{process.env.${constants.OAUTH_CLIENT_SECRET}}}`,
        grant_type: 'authorization_code',
        redirect_uri: '{{bundle.inputData.redirect_uri}}',
      },
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        accept: 'application/json',
      },
    },
    refreshAccessToken: {
      method: 'POST',
      body: {
        refresh_token: '{{bundle.authData.refresh_token}}',
        grant_type: 'refresh_token',
      },
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        accept: 'application/json',
      },
    },
  },
  session: {
    perform: {
      method: 'POST',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        accept: 'application/json',
      },
      body: {
        username: '{{bundle.authData.username}}',
        password: '{{bundle.authData.password}}',
      },
    },
  },
  none: {},
};
